// ----------------------------------------------------------------------

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = "/auth";
const ROOTS_DASHBOARD = "/dashboard";

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, "/login"),
  loginUnprotected: path(ROOTS_AUTH, "/login-unprotected"),
  register: path(ROOTS_AUTH, "/register"),
  registerUnprotected: path(ROOTS_AUTH, "/register-unprotected"),
  resetPassword: path(ROOTS_AUTH, "/reset-password"),
  verify: path(ROOTS_AUTH, "/verify")
};

export const PATH_PAGE = {
  // The sales portal for reps: signed in only, outside the customer dashboard (no onboarding, no paid-plan gate)
  salesPortal: "/sales-portal",
  maintenance: "/maintenance",
  page404: "/404",
  page500: "/500",
  components: "/components",
  bot: "/bot"
};

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,
  general: {
    main: path(ROOTS_DASHBOARD, "/main"),
    contacts: path(ROOTS_DASHBOARD, "/contacts"),
    // The Postgres-backed CRM surface. Runs alongside /contacts, which stays
    // untouched so rollback is a link rather than a deploy.
    crm: path(ROOTS_DASHBOARD, "/crm"),
    // Agency-owner surface: members, roles and the org-wide contact-visibility
    // strategy. Reachable by anyone with the crm feature; the server answers 403
    // to a non-admin and the page renders that as an explanation, not an error.
    crmSettings: path(ROOTS_DASHBOARD, "/crm/settings"),
    // Full-page customer view (עמוד לקוח). `contactId` is the contact Firestore
    // doc id (the international phone number today — treat as opaque). Encoded
    // because phone-shaped ids can carry '+'.
    contactDetail: (contactId: string) =>
      path(ROOTS_DASHBOARD, `/contacts/${encodeURIComponent(contactId)}`),
    importLeads: path(ROOTS_DASHBOARD, "/leads/import"),
    calls: path(ROOTS_DASHBOARD, "/calls"),
    newCalls: path(ROOTS_DASHBOARD, "/newCalls"),
    unansweredCallsBotResponses: path(ROOTS_DASHBOARD, "/unanswered-calls-bot-responses"),
    calendar: path(ROOTS_DASHBOARD, "/calendar"),
    settings: path(ROOTS_DASHBOARD, "/settings"),
    listings: path(ROOTS_DASHBOARD, "/listings"),
    listingsMatch: path(ROOTS_DASHBOARD, "/listingsMatch"),
    rentalToSale: path(ROOTS_DASHBOARD, "/rentalToSale"),
    listingsBoard: path(ROOTS_DASHBOARD, "/listings-board"),
    notifications: path(ROOTS_DASHBOARD, "/notifications"),
    creativesGenerator: path(ROOTS_DASHBOARD, "/creatives-generator"),
    makeovers: path(ROOTS_DASHBOARD, "/makeovers"),
    ai: path(ROOTS_DASHBOARD, "/ai"),
    facebookLeads: path(ROOTS_DASHBOARD, "/facebook-leads"),
    fbPosting: path(ROOTS_DASHBOARD, "/fb-posting"),
    whatsappOfficial: path(ROOTS_DASHBOARD, "/whatsapp"),
    whatsappMessages: path(ROOTS_DASHBOARD, "/whatsapp/messages"),
    whatsappTemplates: path(ROOTS_DASHBOARD, "/whatsapp/templates"),
    whatsappAssistant: path(ROOTS_DASHBOARD, "/whatsapp/assistant"),
    // Legacy "קבוצות תפוצה" URL — redirects to the audiences tab; kept for old links only.
    whatsappGroups: path(ROOTS_DASHBOARD, "/whatsapp/groups"),
    whatsappCampaigns: path(ROOTS_DASHBOARD, "/whatsapp/campaigns"),
    // Audiences (קהלים) — recipient lists, a tab of the WhatsApp hub since they are sent to
    // from there. The campaign wizard lists them as a recipient source; imports and the CRM
    // bulk bar feed them. Formerly `/crm/audiences` (that URL still redirects here).
    whatsappAudiences: path(ROOTS_DASHBOARD, "/whatsapp/audiences"),
    whatsappAudienceDetail: (audienceId: string) =>
      path(ROOTS_DASHBOARD, `/whatsapp/audiences/${encodeURIComponent(audienceId)}`),
    // The blocked-numbers list (חסומים לדיוור), also a hub tab. Governs every send path —
    // official campaigns AND the extension bulk sender — so the route is OR-gated on crm /
    // officialWhatsapp / bulkMessaging rather than on the hub's own feature. Formerly
    // `/crm/blocked` (still redirects here).
    whatsappBlocked: path(ROOTS_DASHBOARD, "/whatsapp/blocked"),
    // Full-page campaign wizard (`?templateDocId=` / `?audienceId=` = entry hand-offs).
    whatsappCampaignNew: path(ROOTS_DASHBOARD, "/whatsapp/campaigns/new"),
    // The sent-messages log — every outbound message on the number, across rails.
    whatsappSentLog: path(ROOTS_DASHBOARD, "/whatsapp/campaigns/log"),
    // Routed campaign control room. `?preview=1` opens the preview/start gate (create handoff).
    whatsappCampaignDetail: (campaignId: string) =>
      path(ROOTS_DASHBOARD, `/whatsapp/campaigns/${encodeURIComponent(campaignId)}`),
    linksControlPanel: path(ROOTS_DASHBOARD, "/links"),
    training: path(ROOTS_DASHBOARD, "/training"),
    // A buyer's minisite in its realtor's EDITOR (the owner bar, the edit dialog, the design panel).
    // The page the buyer opens is the client portal's, at PATH_PUBLIC.myListings; its owner strip links here.
    minisiteEditor: path(ROOTS_DASHBOARD, "/minisite")
  },
  admin: {
    creativesGenerator: path(ROOTS_DASHBOARD, "/admin/creatives-generator"),
    training: path(ROOTS_DASHBOARD, "/admin/training"),
    aiReview: path(ROOTS_DASHBOARD, "/admin/ai-review"),
    feedbacks: path(ROOTS_DASHBOARD, "/admin/feedbacks"),
    coins: path(ROOTS_DASHBOARD, "/admin/coins"),
    signatures: path(ROOTS_DASHBOARD, "/admin/signatures"),
    locations: path(ROOTS_DASHBOARD, "/admin/locations"),
    facebookSourcesStats: path(ROOTS_DASHBOARD, "/admin/facebook-sources-stats"),
    users: path(ROOTS_DASHBOARD, "/admin/users"),
    dbExplorer: path(ROOTS_DASHBOARD, "/admin/db"),
    salesMap: path(ROOTS_DASHBOARD, "/admin/sales-map"),
    salesTraining: path(ROOTS_DASHBOARD, "/admin/sales-training"),
    salesTrainingV2: path(ROOTS_DASHBOARD, "/admin/sales-training-v2"),
    salesRecruiting: path(ROOTS_DASHBOARD, "/admin/recruiting")
  },
  whatsAppWeb: {
    status: path(ROOTS_DASHBOARD, "/whatsAppWeb/status"),
    sendMessages: path(ROOTS_DASHBOARD, "/whatsAppWeb/sendMessages")
  },
  autoreplyBotSettings: {
    autoreplyBot: path(ROOTS_DASHBOARD, "/autoreplyBotSettings/bot-autoreply")
  },
  signatures: {
    root: path(ROOTS_DASHBOARD, "/signatures"),
    sent: path(ROOTS_DASHBOARD, "/signatures/sent"),
    new: path(ROOTS_DASHBOARD, "/signatures/new"),
    templates: path(ROOTS_DASHBOARD, "/signatures/templates"),
    marketplace: path(ROOTS_DASHBOARD, "/signatures/marketplace")
  },
  // Backwards-compat alias used by legacy signature pages that are no longer
  // routed but still compiled. Can be removed once old pages are fully deleted.
  // Maps all legacy signature.* paths to the new /signatures root.
  signature: {
    send: path(ROOTS_DASHBOARD, "/signatures"),
    templates: path(ROOTS_DASHBOARD, "/signatures/templates"),
    manage: path(ROOTS_DASHBOARD, "/signatures"),
    formBuilder: path(ROOTS_DASHBOARD, "/signatures/templates"),
    uploadInstructions: path(ROOTS_DASHBOARD, "/signatures/templates")
  }
};

export const PATH_PUBLIC = {
  listings: "/listings",
  myListings: "/myListings",
  sign: "/sign"
};

export const PATH_DOCS = "https://docs-minimals.vercel.app/introduction";
